import { Grid } from '@mui/material';
import { compareDates, dateToFormatCreateRouteDrivers, getWeekday } from '@nx-smartmonkey/shared/helpers';
import { HSpacer, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { PlusIcon } from '../../../../assets/svg/PlusIcon';
import ReorderRouteIcon from '../../../../assets/svg/route-actions-menu/ReorderRouteIcon';
import { SelectPill } from '../../../../components/SelectPill';
import { SlidingMenu } from '../../../../components/SlidingMenu';
import { StyledFooterButton } from '../../../../components/StyledFooterButton';
import * as routing from '../../../../constants/Routing';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { useRetrieveActiveLanguage } from '../../../../hooks/translations/useRetrieveActiveLanguage';
import InputText from '../../../../react-components/InputText';
import { Text } from '../../../../react-components/Text';
import { usePermissions } from '../../hooks/usePermissions';
import { useUpgradeDialogVisibility } from '../../hooks/useUpgradeDialogVisibility';
import { PRIVATE_COMPONENT_ID } from '../../PrivateComponent';
import { useRouteContext } from '../../route/context/context';
import { useCreateRoute } from '../../route/hooks/useCreateRoute';
import { useSearchDailyRoutes } from '../hooks/useSearchDailyRoutes';
import { CopyStopsRouteCreation } from './CopyStopsRouteCreation';
import { CreateRouteFieldContainer } from './CreateRouteFieldContainer';
import { RoutalDatePicker } from './RoutalDatePicker';

interface RouteCreationProps {
  isOpen: boolean;
  onClose: () => void;
  initialDate?: Date;
  beforeLeave?: () => void;
}

export const RouteCreation = ({ isOpen, onClose, initialDate, beforeLeave }: RouteCreationProps) => {
  const navigate = useNavigate();
  const { routeState } = useRouteContext();
  const { activeLanguage } = useRetrieveActiveLanguage();
  const { run: createRoute, success, isLoading } = useCreateRoute();
  const [executionDate, setExecutionDate] = useState<Date>(initialDate ?? new Date());
  const { isSoloDriver, hasSubscription } = usePermissions();
  const { data: dayHasRoutes, isLoading: dailyRoutesLoading } = useSearchDailyRoutes({
    day: executionDate,
    enabled: isOpen && Boolean(isSoloDriver) && !hasSubscription,
  });
  const { setDialogVisibility } = useUpgradeDialogVisibility();

  const [planName, setPlanName] = useState<string>(
    getWeekday({
      date: initialDate ?? new Date(),
      language: activeLanguage,
    })
  );
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false);
  const [openCopyStops, setOpenCopyStops] = useState<boolean>(false);

  // Selected date logic
  const todayDate = new Date();
  const tomorrowDate = new Date(todayDate.getTime() + 24 * 60 * 60 * 1000);
  const [lastSelectedDate, setLastSelectedDate] = useState<Date | undefined>(undefined);

  const cantCreatePlan = isSoloDriver && !hasSubscription && (dayHasRoutes || dayHasRoutes === undefined);

  useEffect(() => {
    if (initialDate && initialDate !== executionDate) {
      setExecutionDate(initialDate);
      setPlanName(
        getWeekday({
          date: initialDate,
          language: activeLanguage,
        })
      );

      // If execution date is not today neither tomorrow, we set it as the last selected date
      if (!compareDates(todayDate, initialDate) && !compareDates(tomorrowDate, initialDate)) {
        setLastSelectedDate(initialDate);
      }
    }
    return () => {
      setExecutionDate(new Date());
      setPlanName(
        getWeekday({
          date: new Date(),
          language: activeLanguage,
        })
      );
      setLastSelectedDate(undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDate]);

  useEffect(() => {
    if (success && routeState.route?.id) {
      beforeLeave && beforeLeave();
      navigate(routing.PRIVATE_ROUTES.ROUTE_DETAILS.replace(`:routeId`, routeState.route?.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  const element = document.getElementById(PRIVATE_COMPONENT_ID);
  if (!element) {
    return null;
  }

  return createPortal(
    <>
      <RoutalDatePicker
        value={new Date()}
        isOpen={openDatePicker}
        zIndex={1007}
        onSelect={(date: Date) => {
          setExecutionDate(date);
          setLastSelectedDate(date);
          setOpenDatePicker(false);
        }}
        onCancel={() => setOpenDatePicker(false)}
      />
      <CopyStopsRouteCreation
        isOpen={openCopyStops}
        onClose={() => {
          setOpenCopyStops(false);
        }}
        creationData={{
          label: planName,
          execution_date: executionDate,
        }}
        success={success}
        isLoading={isLoading}
        createRoute={(data) => {
          createRoute({ ...data });
        }}
      />
      <SlidingMenu
        id="route-creation"
        isOpen={isOpen}
        direction="up"
        onClose={onClose}
        slideStyles={{ top: 0, left: 0, zIndex: 1006 }}
        childrenStyles={{ backgroundColor: RoutalPalette.white, padding: 0 }}
        headerStyles={{ backgroundColor: RoutalPalette.white }}
        headerContent={
          <Grid
            style={{
              display: `flex`,
              flexDirection: `row`,
              flexWrap: `nowrap`,
              width: `100%`,
              height: `70px`, // Same as SlidingMenu

              justifyContent: `space-between`,
              alignItems: `center`,
            }}
          >
            <Text variant="h6" isBold intlId="drivers.route.creation.title" />
          </Grid>
        }
        footerContent={
          <Grid
            style={{
              width: `100%`,
              display: `flex`,
              justifyContent: `center`,
              alignItems: `center`,
              height: `70px`,
              zIndex: 1005,
              backgroundColor: RoutalPalette.white,
              padding: `0 14px`,
            }}
          >
            <StyledFooterButton
              onClick={() => {
                if (dailyRoutesLoading) return; // I did not add it to disabled because it feel bad using it
                if (cantCreatePlan) {
                  setDialogVisibility(true);
                } else {
                  createRoute({ label: planName === `` ? undefined : planName, execution_date: executionDate });
                }
              }}
              disabled={isLoading || success}
            >
              <PlusIcon color={RoutalPalette.white} />
              <HSpacer small />
              {intl.formatMessage({ id: `drivers.route.create` })}
            </StyledFooterButton>
          </Grid>
        }
      >
        <Grid
          style={{
            height: `100%`,
            width: `100%`,
            background: RoutalPalette.white,
            padding: `24px 20px 24px 20px`,
          }}
        >
          <Grid
            style={{
              height: `100%`,
              width: `100%`,
              background: RoutalPalette.white,
            }}
          >
            <Grid style={{ opacity: isLoading ? 0.5 : 1, pointerEvents: isLoading ? `none` : `auto` }}>
              <CreateRouteFieldContainer key={`route-creation-name`} textId={`drivers.route.creation.name`}>
                <InputText
                  placeholderIntlId="drivers.route.creation.name_ph"
                  fieldValue={planName || ``}
                  onChange={(value) => setPlanName(value)}
                  variant="outlined"
                  inputContainerStyles={{ width: `100%`, height: `40px` }}
                  disabled={isLoading}
                  inputStyles={{
                    width: `100%`,
                    borderColor: RoutalPalette.grey7,
                    color: planName ? RoutalPalette.text : undefined,
                  }}
                />
              </CreateRouteFieldContainer>
              <VSpacer medium />
              <CreateRouteFieldContainer key={`route-creation-day`} textId={`drivers.route.creation.day_select`}>
                <Grid item style={{ display: `contents`, width: `100%` }}>
                  <SelectPill
                    selected={compareDates(todayDate, executionDate)}
                    onClick={() => setExecutionDate(todayDate)}
                  >
                    <Text intlId="drivers.route.creation.today" />
                    <HSpacer small />
                    <Text variant="label">{dateToFormatCreateRouteDrivers(todayDate, activeLanguage)}</Text>
                  </SelectPill>
                  <VSpacer small />
                  <SelectPill
                    selected={compareDates(tomorrowDate, executionDate)}
                    onClick={() => setExecutionDate(tomorrowDate)}
                  >
                    <Text intlId="drivers.route.creation.tomorrow" />
                    <HSpacer small />
                    <Text variant="label">{dateToFormatCreateRouteDrivers(tomorrowDate, activeLanguage)}</Text>
                  </SelectPill>
                  <VSpacer small />
                  {lastSelectedDate && (
                    <>
                      <SelectPill
                        selected={compareDates(lastSelectedDate, executionDate)}
                        onClick={() => setExecutionDate(lastSelectedDate)}
                      >
                        <Text>{lastSelectedDate.toLocaleDateString()}</Text>
                        <HSpacer small />
                        <Text variant="label">{dateToFormatCreateRouteDrivers(lastSelectedDate, activeLanguage)}</Text>
                      </SelectPill>
                      <VSpacer small />
                    </>
                  )}
                  <SelectPill radialIcon={false} onClick={() => setOpenDatePicker(true)}>
                    <Text intlId="drivers.route.creation.day_select" />
                  </SelectPill>
                </Grid>
              </CreateRouteFieldContainer>
              <VSpacer medium />

              <CreateRouteFieldContainer key={`route-creation-move`} textId={`drivers.route.creation.move_stops`}>
                <SelectPill
                  radialIcon={false}
                  onClick={() => {
                    if (dailyRoutesLoading) return;
                    if (cantCreatePlan) {
                      setDialogVisibility(true);
                    } else {
                      setOpenCopyStops(true);
                    }
                  }}
                >
                  <ReorderRouteIcon color={RoutalPalette.secondary20} />
                  <HSpacer small />
                  <Text intlId="drivers.route.creation.stops_select" />
                </SelectPill>
              </CreateRouteFieldContainer>
            </Grid>
          </Grid>
        </Grid>
      </SlidingMenu>
    </>,
    element as HTMLElement
  );
};
